'use client';

import ModalDialogue from '@koda/koda-components/molecules/ModalDialogue';
import { SearchInput } from '@koda/koda-components/molecules/SearchInput';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useArtistsSearch } from '../../../../../../src/lib/api/hooks/artists';
import { Artist } from '../../../../../../src/lib/api/models/artists';
import { openContactUsMailto } from '../../../utils/contactUsMailto';
import { useLicenseStore } from '../../licenseStore';

const ArtistInput = ({
  onChange,
  error,
  artists: artistsProp = [],
  disabled,
  value,
  name = '',
}: {
  onChange: (value?: Artist) => void;
  error?: string;
  disabled?: boolean;
  artists?: Artist[];
  value?: string;
  name: string;
}) => {
  const t = useTranslations('licenses.components.input.artistInput');
  const router = useRouter();
  const { licenseInformation } = useLicenseStore();

  const [searchValue, setSearchValue] = useState('');

  const {
    data,
    isLoading,
    isValidating,
    error: searchError,
  } = useArtistsSearch({
    searchString: searchValue,
    enabled: !disabled,
    debounceMs: 500,
    top: 20,
    minLength: 1,
  });

  const artists = data ?? artistsProp;
  const currentItem: Artist | undefined = value
    ? (artists.find(a => a.value === value) ?? {
        number: value,
        searchName: name,
        name,
        value,
        text: name,
      })
    : undefined;

  return (
    <SearchInput<Artist>
      items={artists}
      currentItem={currentItem}
      allowCustom={true}
      minSearchLength={1}
      normalizeCustom={(text, val) => ({
        number: val,
        searchName: text,
        name: text,
        value: val,
        text,
      })}
      isLoading={isLoading}
      isValidating={isValidating}
      searchError={searchError}
      searchValue={searchValue}
      onSearchValueChange={setSearchValue}
      value={value}
      onChange={onChange}
      disabled={disabled}
      error={error}
      label={t('label')}
      placeholder={t('placeholder')}
      searchPlaceholder={t('searchPlaceholder')}
      choseCustomText={t('choseCustomText')}
      suggestCustomText={t('suggestCustomText')}
      description={t('description')}
      searchingText={t('searchText')}
      emptyText={t('emptyListText')}
      noResultsText={t('noResultsText')}
      technicalErrorText={t('technicalErrorText')}
      renderErrorModal={(open, onClose) => (
        <ModalDialogue
          title={t('technicalErrorModal.title')}
          description={t('technicalErrorModal.description')}
          type="error"
          primaryText={t('technicalErrorModal.sendEmailButton')}
          cancelText={t('technicalErrorModal.goToFrontPageButton')}
          open={open}
          isClosable={true}
          onOpenChange={isOpen => {
            if (!isOpen) onClose();
          }}
          onPrimaryAction={() => {
            openContactUsMailto({
              subject: t('technicalErrorModal.emailSubject', { licenseName: licenseInformation?.licenseCode ?? '' }),
              body: t('technicalErrorModal.emailBody', {
                licenseName: licenseInformation?.licenseCode ?? '',
                errorCode: searchError?.message ?? '',
              }),
            });
            onClose();
          }}
          onCancelAction={() => {
            onClose();
            router.push('/');
          }}
        />
      )}
    />
  );
};

export default ArtistInput;
